import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { type QueryWishlistsArgs } from '@pkg/graphql/codegen/graphql.ts';

import { callWishlist } from './request';

interface UseGetWishlistProps {
    variables: QueryWishlistsArgs & { includeContents?: boolean };
}

export const USER_WISHLISTS = 'USER_WISHLISTS';

export function useGetWishlist({ variables }: UseGetWishlistProps) {
    variables.pagination = {
        page: {
            limit: 1,
            page: 0,
        },
    };

    return useQuery({
        queryKey: [
            USER_WISHLISTS,
            variables.includeContents,
            variables.filter.owned?.primaryYat,
            variables.filter.popular,
            variables.filter.following,
            ...(variables.filter.slugs || []),
        ],
        queryFn: () => callWishlist({ variables }),
        select: (res) => {
            if (res.data) {
                return res.data.wishlists.value?.[0];
            }
        },
        enabled: Boolean(
            variables.filter.slugs?.length ||
                variables.filter.owned?.internalId ||
                variables.filter.popular ||
                variables.filter.following,
        ),
    });
}

export function useGetWishlists({
    variables,
    limit,
    disabled = false,
}: UseGetWishlistProps & { limit: number; disabled?: boolean }) {
    return useInfiniteQuery({
        queryKey: [
            USER_WISHLISTS,
            limit,
            variables.includeContents,
            variables.filter.owned?.primaryYat,
            variables.filter.popular,
            variables.filter.following,
            ...(variables.filter.slugs || []),
        ],
        queryFn: ({ pageParam }) => {
            const vars = {
                ...variables,
                pagination: {
                    page: {
                        limit,
                        page: pageParam,
                    },
                },
            };

            return callWishlist({ variables: vars });
        },
        initialPageParam: 0,
        getNextPageParam: (res) => {
            if (res.data?.wishlists?.value?.length) {
                return (res.data?.wishlists?.page || 0) + 1;
            }
            return null;
        },
        enabled:
            !disabled &&
            Boolean(
                variables.filter.slugs?.length ||
                    variables.filter.owned?.internalId ||
                    variables.filter.popular ||
                    variables.filter.following,
            ),
    });
}
